import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { Component } from '@theme/component';

// "region" = handleized surface_name ("shaft", "front", …) — the canonical runtime
// identifier across JS state, event detail, DOM data-position, and GLB mesh prefixes.
// "surface_N" is Shopify metafield storage only and never leaks into runtime.

// JS fallback for the `rarity_tier` metaobject. Source of truth: bunny-tokens.liquid
// emits `--rarity-{cost}-rgb` + `--rarity-{cost}-opacity` at `:root`. Keep cost keys
// in sync with COST_TO_RARITY in swatch-picker.js.
const RARITY_CONFIG = {
  1: { hex: 0x6b7280, intensity: 0.35 },
  2: { hex: 0x22c55e, intensity: 0.50 },
  3: { hex: 0x3b82f6, intensity: 0.65 },
  4: { hex: 0xa855f7, intensity: 0.80 },
  5: { hex: 0xf59e0b, intensity: 0.95 },
  6: { hex: 0xec4899, intensity: 1.00 },
};

// Camera at (0,0,4) looking at origin → phi = π/2 is upright.
const UPRIGHT_PHI = Math.PI / 2;
// 138° lands the seam at the model's back-left so the front face is
// camera-friendly on first paint regardless of how the GLB was authored.
const INITIAL_MODEL_Y_ROTATION = THREE.MathUtils.degToRad(138);
const RETURN_DURATION_MS = 800;

// Shopify Draco-compresses uploaded GLBs; without DRACOLoader, GLTFLoader silently
// fails to parse them. Self-host before launch.
const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';

// Soda Can comparison mode. wu/in = toy auto-fit height / variant `total_length_in`;
// can scales so its real 4.83" height lands at that same factor.
const SODA_CAN_URL = 'https://cdn.shopify.com/3d/models/ce578cbde7702cf0/BunnyWorks_KnottyBerry.glb';
const SODA_CAN_HEIGHT_IN = 4.83;
const SODA_CAN_GAP_IN = 0.2;
// Camera DISTANCE from orbit target (not position.z) — OrbitControls preserves
// radius, so setting position.z directly drifts unpredictably across toggles.
const CAMERA_DISTANCE_SOLO = 4;
const CAMERA_DISTANCE_PAIR = 5.5;
const TOGGLE_TWEEN_MS = 400;
const FALLBACK_TOY_LENGTH_IN = 6.5;

// Maps swatch_type name → fragment-shader uSwatchType branch index. Index
// 3 reserved for Ribbons (shader branch TBD). Authored types not in this
// map — currently `ribbon-marble`, `banded` — fall through to index 0
// (Solid) via the `?? 0` in #paintMaterial. Reconcile when the Ribbons /
// ribbon-marble / banded shaders land.
const SWATCH_TYPE = { solid: 0, faded: 1, mottled: 2, ribbons: 3, splotch: 4, glow: 5 };

// Glow pulse range: [uGlowBase, uGlowBase + uGlowAmp]. Base keeps a faint floor so
// the region never reads as fully unlit.
const GLOW_BASE = 0.12;
const GLOW_AMP = 0.88;

const BLOOM_LAYER = 1;
const BLOOM_STRENGTH = 0.3;
const BLOOM_RADIUS = 0.5;
const BLOOM_THRESHOLD = 0.35;

// While any region is Glow, ease the three-point rig + IBL toward LIGHT_DIM
// over ~400ms (matches the .section-background CSS fade).
const LIGHT_BRIGHT = { hemi: 0.35, key: 1.6, fill: 0.55, rim: 0.7, envMap: 0.85, exposure: 1.05 };
const LIGHT_DIM = { hemi: 0.28, key: 1.25, fill: 0.42, rim: 0.55, envMap: 0.70, exposure: 0.95 };
const DIM_TIME_CONSTANT_S = 0.12;

// Single source of truth — key light position. Used at scene setup AND
// for the sparkle shader's uKeyLightDir uniform; reading both from the
// same constant keeps the rig and shader from drifting apart.
const KEY_LIGHT_POSITION = new THREE.Vector3(2.5, 3, 2);

const AUTO_ROTATE_SPEED = 1.5;


// Exposes object-space + world-space position to the fragment stage.
// Object-space for Faded/Mottled/Splotch (continuous across surface seams);
// world-space for Sparkle (uniform cell size across submeshes).
const VERT_DECLS = `
  #include <common>
  varying vec3 vObjectPos;
  varying vec3 vWorldPos;
`;
const VERT_BODY = `
  #include <begin_vertex>
  vObjectPos = position;
  vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
`;

// 3D simplex noise (Ashima webgl-noise, public domain). `bunny_`-prefixed
// to dodge any collision with Three.js stock chunk names.
const SIMPLEX_NOISE_3D = `
  vec3 bunny_mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 bunny_mod289_4(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 bunny_permute(vec4 x) { return bunny_mod289_4(((x*34.0)+1.0)*x); }
  vec4 bunny_taylor_inv_sqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  float bunny_snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = bunny_mod289(i);
    vec4 p = bunny_permute(bunny_permute(bunny_permute(
               i.z + vec4(0.0, i1.z, i2.z, 1.0))
             + i.y + vec4(0.0, i1.y, i2.y, 1.0))
             + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = bunny_taylor_inv_sqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
`;

// Fragment shader injection. uSwatchType branches diffuse color per-fragment;
// Three's standard chunks then apply normals / env IBL / clearcoat / tone-map on top.
// Faded: breathing band along the long axis. Mottled: lava-lamp at MOTTLE_SCALE.
// Splotch: low-frequency single hump 3D-wandered by three incommensurate sin waves.
const FRAG_DECLS = `
  #include <common>
  #define BREATH_RATE 0.6
  #define BREATH_AMPLITUDE 0.1
  #define BREATH_EDGE 0.22
  // Tri-band fade (custom 3-colour): tighter bands so three read cleanly.
  #define BREATH_AMPLITUDE_TRI 0.07
  #define BREATH_EDGE_TRI 0.14
  #define MOTTLE_SCALE 6.0
  #define MOTTLE_DRIFT 0.15
  #define MOTTLE_CONTRAST_LO 0.4
  #define MOTTLE_CONTRAST_HI 0.6
  #define SPLOTCH_SCALE 1.5
  #define SPLOTCH_DRIFT 0.15
  #define SPLOTCH_WANDER 1.2
  #define SPLOTCH_CONTRAST_LO 0.55
  #define SPLOTCH_CONTRAST_HI 0.7
  #define GLOW_RATE 1.6
  #define GLOW_BOOST 0.35
  uniform int uSwatchType;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  // Custom 3-colour fades: middle band colour + flag. uHasMid gates the
  // tri path so 2-colour fades keep the exact original two-band look.
  uniform vec3 uColorMid;
  uniform float uHasMid;
  uniform float uTime;
  uniform float uTip;
  uniform float uBase;
  // Mottled/Splotch use union-span so blob size stays consistent across
  // mode toggles. Faded swaps region↔union per mode via uTip/uBase.
  uniform float uModelHeight;
  uniform float uGlowBase;
  uniform float uGlowAmp;
  #define SPARKLE_DENSITY    480.0
  #define SPARKLE_SHARP      50.0
  #define SPARKLE_RATE       3.5
  #define SPARKLE_GAIN       2.5
  #define SPARKLE_TINT       0.30
  #define SPARKLE_HUE_AMOUNT 1.0
  #define SPARKLE_MAX_MIX    0.85
  uniform float uSparkle;
  uniform vec3 uKeyLightDir;
  varying vec3 vObjectPos;
  varying vec3 vWorldPos;
${SIMPLEX_NOISE_3D}
  // Cheap 3D-to-1D hash — three calls give a randomly oriented facet
  // normal per cell, much cheaper than simplex.
  float bunny_hash13(vec3 p) {
    p = fract(p * vec3(443.8975, 397.2973, 491.1871));
    p += dot(p, p.yzx + 19.19);
    return fract((p.x + p.y) * p.z);
  }
`;
const FRAG_COLOR = `
  #include <color_fragment>
  vec3 procColor = uColor1;
  if (uSwatchType == 1) {
    float span = max(uTip - uBase, 1e-5);
    float t = clamp((vObjectPos.y - uBase) / span, 0.0, 1.0);
    float pulse = sin(uTime * BREATH_RATE);
    if (uHasMid > 0.5) {
      // Three bands (base / mid / tip), two breathing boundaries at the
      // thirds. Phase-offset pulses so the middle band organically widens
      // and narrows rather than sliding as a rigid unit.
      float pulseHi = sin(uTime * BREATH_RATE + 1.7);
      float centerLo = 0.3333 + pulse * BREATH_AMPLITUDE_TRI;
      float centerHi = 0.6667 + pulseHi * BREATH_AMPLITUDE_TRI;
      float blendLo = smoothstep(centerLo - BREATH_EDGE_TRI, centerLo + BREATH_EDGE_TRI, t);
      float blendHi = smoothstep(centerHi - BREATH_EDGE_TRI, centerHi + BREATH_EDGE_TRI, t);
      procColor = mix(mix(uColor2, uColorMid, blendLo), uColor1, blendHi);
    } else {
      float center = 0.5 + pulse * BREATH_AMPLITUDE;
      float blend = smoothstep(center - BREATH_EDGE, center + BREATH_EDGE, t);
      procColor = mix(uColor2, uColor1, blend);
    }
  } else if (uSwatchType == 2) {
    float span = max(uModelHeight, 1e-5);
    vec3 p = (vObjectPos / span) * MOTTLE_SCALE;
    p.y -= uTime * MOTTLE_DRIFT;
    float n = bunny_snoise(p) * 0.5 + 0.5;
    float blend = smoothstep(MOTTLE_CONTRAST_LO, MOTTLE_CONTRAST_HI, n);
    procColor = mix(uColor2, uColor1, blend);
  } else if (uSwatchType == 4) {
    float span = max(uModelHeight, 1e-5);
    vec3 p = (vObjectPos / span) * SPLOTCH_SCALE;
    // Three incommensurate frequencies so the wander never repeats.
    p += vec3(
      sin(uTime * SPLOTCH_DRIFT * 0.91),
      sin(uTime * SPLOTCH_DRIFT * 1.13),
      sin(uTime * SPLOTCH_DRIFT * 1.07)
    ) * SPLOTCH_WANDER;
    float n = bunny_snoise(p) * 0.5 + 0.5;
    float blend = smoothstep(SPLOTCH_CONTRAST_LO, SPLOTCH_CONTRAST_HI, n);
    procColor = mix(uColor1, uColor2, blend);
  } else if (uSwatchType == 5) {
    // Glow — dim body color so the emissive pulse reads as light through silicone.
    procColor = uColor1 * 0.35;
  }
  // Sparkle — composites on top of any swatch-type branch. Each fragment
  // falls into a world-space cell with a randomly oriented facet normal;
  // sharp pow on half-vector specular makes the pinpoint glint, per-cell
  // phase-shifted sin twinkles each speck on its own clock. Stays in
  // procColor (never emissive) so the no-Glow fast render path is preserved.
  if (uSparkle > 0.5) {
    vec3 cell = floor(vWorldPos * SPARKLE_DENSITY);
    vec3 facetN = normalize(vec3(
      bunny_hash13(cell)         * 2.0 - 1.0,
      bunny_hash13(cell + 17.13) * 2.0 - 1.0,
      bunny_hash13(cell + 31.71) * 2.0 - 1.0
    ));
    // Half-vector specular: flakes glint when light reflects off them
    // toward the camera, not just when the camera faces the facet.
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    vec3 halfDir = normalize(uKeyLightDir + viewDir);
    float align = max(dot(facetN, halfDir), 0.0);
    float spark = pow(align, SPARKLE_SHARP);
    // viewPhase couples twinkle to camera rotation — sparkles "wake up"
    // as you turn the model. pow(max(sin)) shapes each cycle into a brief
    // bright peak rather than a slow rise/fall.
    float phase = bunny_hash13(cell + 7.77) * 6.2831853;
    float viewPhase = dot(viewDir, vec3(11.0, 13.0, 17.0));
    float twinkle = pow(max(sin(uTime * SPARKLE_RATE + phase + viewPhase), 0.0), 6.0);
    spark *= 0.45 + 0.55 * twinkle;
    spark = clamp(spark * SPARKLE_GAIN, 0.0, 1.0);
    // Per-cell hue shift via phase-shifted RGB cosine. Pulled toward the
    // local procColor to harmonize with the swatch, then darkened on very
    // bright bases so glints never match-and-vanish.
    float cellPhase = bunny_hash13(cell + 13.13) * 6.2831853;
    vec3 hueColor = 0.5 + 0.5 * cos(vec3(cellPhase, cellPhase + 2.094, cellPhase + 4.189));
    hueColor = mix(hueColor, procColor, 0.35);
    float localLum = dot(procColor, vec3(0.299, 0.587, 0.114));
    float darkenForBright = smoothstep(0.45, 0.75, localLum);
    hueColor *= mix(1.0, 0.1, darkenForBright);
    float hueMix = smoothstep(0.3, 0.7, localLum) * SPARKLE_HUE_AMOUNT;
    // Anchor to procColor (not uColor1) so sparkle rides whatever the
    // underlying swatch produced — Faded gradients, Mottled noise, etc.
    vec3 sparkBase = mix(procColor * 1.7, vec3(1.0), SPARKLE_TINT);
    vec3 sparkColor = mix(sparkBase, hueColor, hueMix);
    procColor = mix(procColor, sparkColor, spark * SPARKLE_MAX_MIX);
  }
  diffuseColor.rgb = procColor;
`;

// Glow emissive — runs after the standard <emissivemap_fragment> so any built-in
// emissive applies first, then we pulse on top. GLOW_BOOST pushes above the bloom
// threshold; bloom RT harvests only fragments above ~1.0.
const FRAG_EMISSIVE = `
  #include <emissivemap_fragment>
  if (uSwatchType == 5) {
    float pulse = uGlowBase + uGlowAmp * (0.5 + 0.5 * sin(uTime * GLOW_RATE));
    totalEmissiveRadiance += uColor1 * pulse * GLOW_BOOST;
  }
`;

class Bunny3dViewer extends Component {
  // Gallery renders product-media in multiple slots (main + zoom clone); first
  // mount wins, subsequent mounts mark themselves duplicate and stay inert.
  static #activeMediaIds = new Set();

  // Cache live WebGL state across Horizon's media-gallery replaceWith on
  // variant:update. Canvas / renderer / scene / materials survive intact —
  // no GLB reload on every Pour Style change.
  static #stateCache = new Map();

  // Defensive FIFO cap (drops the oldest insertion on overflow, not the
  // least-recently-touched — sufficient since normal flows drain to 0–2
  // entries via the delete-on-read in #adoptCachedState; this only
  // triggers if some future morph behavior orphans entries).
  static #STATE_CACHE_MAX = 8;

  // Single-region fallback aliases the same material across every surface
  // entry — naive iteration would repaint/dispose the same GPU resource N
  // times.
  static #dedupedMaterials(entries) {
    const seen = new Set();
    for (const entry of entries) {
      if (entry.material) seen.add(entry.material);
    }
    return seen;
  }

  // Dispose every material + geometry under a soda-can scene graph,
  // skipping materials already in `seen` (shared with the toy's surfaces).
  // Mutates `seen` so subsequent callers don't double-dispose. Shared by
  // both the live-disconnect path and the cache-eviction path.
  static #disposeSodaCanScene(sodaCanRoot, seen) {
    if (!sodaCanRoot) return;
    sodaCanRoot.traverse((child) => {
      if (!child.isMesh) return;
      if (child.material && !seen.has(child.material)) {
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => { if (!seen.has(m)) { m.dispose(); seen.add(m); } });
        } else {
          child.material.dispose();
          seen.add(child.material);
        }
      }
      child.geometry?.dispose();
    });
  }

  static #disposeCachedState(state) {
    if (!state) return;
    const seen = Bunny3dViewer.#dedupedMaterials(state.surfaces?.values() ?? []);
    for (const material of seen) material.dispose();
    Bunny3dViewer.#disposeSodaCanScene(state.sodaCanRoot, seen);
    state.renderer?.dispose();
  }

  // Lazy single-fetch promise for the soda can GLB — per-viewer copies come
  // from `gltf.scene.clone(true)`. Two viewers on one page amortize the fetch.
  static #sodaCanGltfPromise = null;

  // Shared PMREM-convolved environment map. PMREM compute is 5–20ms; cache
  // once, every viewer binds the same THREE.Texture. Texture color-space is
  // baked at first compute (currently SRGB on all viewers).
  static #envMapTexture = null;
  static #ensureEnvMap(renderer) {
    if (Bunny3dViewer.#envMapTexture) return Bunny3dViewer.#envMapTexture;
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envScene = new RoomEnvironment();
    Bunny3dViewer.#envMapTexture = pmrem.fromScene(envScene, 0.04).texture;
    pmrem.dispose();
    return Bunny3dViewer.#envMapTexture;
  }

  #renderer = null;
  #scene = null;
  #camera = null;
  #controls = null;
  // Per-region state keyed by surface_name handle. Key set comes from
  // `data-surfaces` (variant's split surfaces; "model" is the synthetic
  // union and is never a key here). Entry: { material, meshes, bounds, isGlow }.
  // Single-region fallback aliases the same material across every entry.
  #surfaces = new Map();
  #rafId = 0;
  #paused = false;
  // Multi-product hosts mount inactive panels with data-defer-load="true";
  // resume() fires the GLB load on first activation.
  #deferredLoadPending = false;
  #modelLoadStarted = false;
  // True at end of #onModelLoaded. The disconnect cache-write branch treats
  // in-flight loads like neverLoaded so the new mount fires its own load.
  #modelLoadCompleted = false;
  #startedAt = 0;
  #lastTickAt = 0;
  #resizeObserver = null;
  // Auto-pauses the rAF loop when scrolled out of viewport. #offScreenPaused
  // tracks whether THIS observer paused us, so an explicit host pause()
  // supersedes auto-resume on scroll-back.
  #intersectionObserver = null;
  #offScreenPaused = false;
  #onSwatchChangeBound = null;
  #onRarityChangeBound = null;
  #mobileMediaQuery = null;
  #onMobileMediaChangeBound = null;
  #ownsMediaId = false;
  #defaultAutoRotate = true;
  #pointerLeaveBound = null;
  // Pointer-leave: phi eases back to upright; theta stays where the user left it.
  #returning = false;
  #returnStartedAt = 0;
  #returnFromPhi = 0;
  #returnSphericalScratch = null;
  #returnOffsetScratch = null;
  // Scratch for #setCameraDistance — independent of #returnOffsetScratch
  // because #stepReturn and #stepToggleTween can both run in the same #tick.
  #cameraOffsetScratch = null;
  // Selective bloom — only runs while at least one region is Glow. Otherwise
  // bypass both composers and call renderer.render directly for status-quo perf.
  #bloomComposer = null;
  #finalComposer = null;
  // Material-swap during bloom: non-glow meshes get #darkMaterial so depth
  // occludes glow behind opaque geometry. Originals stashed in #savedMaterials.
  #darkMaterial = null;
  #savedMaterials = new Map();
  #bloomLayerCheck = null;
  #darkenBound = null;
  #restoreBound = null;
  // Cached so #stepDim can drive intensities each frame without scene traversal.
  // #dimTarget = 1 while any region is Glow; #dimCurrent eases between
  // LIGHT_BRIGHT and LIGHT_DIM.
  #hemiLight = null;
  #keyLight = null;
  #fillLight = null;
  #rimLight = null;
  #dimCurrent = 0;
  #dimTarget = 0;
  // While Glow, override --color-background to black on the surrounding
  // section so consumers inherit "lights off in here" without each needing
  // to know about glow state.
  #sectionEl = null;
  #productInfoEl = null;
  // Section-scoped event bus + halo-var target. Picker / rotator / premade
  // dispatch with bubbles+composed, bubble up to this wrapper, and stop —
  // sibling sections never see them. Wrapper survives Horizon's morph.
  #scopeRoot = null;
  // The pour-configurator panel this viewer lives in (or null on PDP / hero).
  // Used by #originMatches to reject sibling-panel events, and as the halo
  // var target — section-scoped writes would let the last writer win across
  // all 4 panels.
  #hostPanel = null;
  // section.id + media id. Two sections rendering the same product share
  // data-media-id but need independent pipelines — keying by mediaId alone
  // would make the second viewer bail as a duplicate.
  #cacheKey = null;
  // Cached + restored across morph because copyAttributes strips inline halo
  // vars from the section. #adoptCachedState re-applies synchronously before
  // paint to avoid a fade out + back in.
  #lastRarityCost = null;
  // The host element we wrote halo vars to in our last #applyRarity. Tracked
  // so disconnectedCallback clears the same element we wrote to, even if
  // hostPanel / scopeRoot have changed shape since. Prevents a fallback-to-
  // documentElement-disconnect from wiping vars set by a sibling viewer.
  #lastHaloHost = null;
  // Memoized result of `any region is Glow` — recomputed at end of
  // #applySelection (where isGlow flags get mutated) and #adoptCachedState
  // (where #surfaces is replaced wholesale).
  #anyGlowCached = false;
  // Soda Can comparison. #applySodaCanLayout adds the can, offsets the toy
  // on +x, tweens the camera. State survives morph; can rescales when Size
  // changes (wu/in derives from `total_length_in`).
  #sodaCanMode = false;
  #sodaCanRoot = null;
  #sodaCanWidthWorld = 0;
  #toyRoot = null;
  #toyCenteredX = 0;
  #toyHeightWorld = 0;
  #toyWidthWorld = 0;
  #toyLengthIn = NaN;
  #toggleTweenStart = 0;
  #toggleTweenFromDistance = 0;
  #toggleTweenToDistance = 0;
  #toggleTweenFromToyX = 0;
  #toggleTweenToToyX = 0;
  #toggleTweenFromCanX = 0;
  #toggleTweenToCanX = 0;
  // Scale + Y tween — smooth can-resize on size-variant changes (Mini→Queen).
  // Toggle paths leave these pinned, so no scale/Y animation there.
  #toggleTweenFromCanScale = 1;
  #toggleTweenToCanScale = 1;
  #toggleTweenFromCanY = 0;
  #toggleTweenToCanY = 0;
  #onSodaCanToggleBound = null;
  // On PDP the gallery morphs the viewer host on variant:update; on hero +
  // configurator nothing replaces the host, so this listener keeps
  // `data-toy-length-in` in sync with the new variant.
  #onVariantUpdateBound = null;

  connectedCallback() {
    super.connectedCallback();

    this.#scopeRoot = this.closest('.shopify-section') || document;
    this.#hostPanel = this.closest('[data-pour-panel]');
    const mediaId = this.dataset.mediaId;
    // Optional suffix lets multiple viewers in one section keep distinct
    // cache slots (configurator panels rendering different products).
    const suffix = this.dataset.cacheKeySuffix ? `:${this.dataset.cacheKeySuffix}` : '';
    this.#cacheKey = mediaId ? `${this.#scopeRoot.id || ''}${suffix}:${mediaId}` : null;
    // `data-paused` is the multi-panel handoff with pour-product-tabs.js:
    // inactive panels mount paused; pour-product-tabs calls .pause()/.resume() on tab switch.
    this.#paused = this.dataset.paused === 'true';

    if (this.#cacheKey) {
      const cached = Bunny3dViewer.#stateCache.get(this.#cacheKey);
      if (cached) {
        Bunny3dViewer.#stateCache.delete(this.#cacheKey);
        Bunny3dViewer.#activeMediaIds.add(this.#cacheKey);
        this.#ownsMediaId = true;
        this.#adoptCachedState(cached);
        return;
      }

      if (Bunny3dViewer.#activeMediaIds.has(this.#cacheKey)) {
        this.dataset.state = 'duplicate';
        return;
      }
      Bunny3dViewer.#activeMediaIds.add(this.#cacheKey);
      this.#ownsMediaId = true;
    }

    const canvas = this.querySelector('canvas');
    if (!canvas) return;

    this.#renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.#renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.#renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.#renderer.toneMappingExposure = LIGHT_BRIGHT.exposure;

    this.#scene = new THREE.Scene();
    this.#scene.environment = Bunny3dViewer.#ensureEnvMap(this.#renderer);

    this.#camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    this.#camera.position.set(0, 0, 4);

    // Three-point rig over the IBL: hemi (sky bounce) + warm key + cool fill + warm rim.
    const hemi = new THREE.HemisphereLight(0xffffff, 0x33334a, LIGHT_BRIGHT.hemi);
    this.#scene.add(hemi);
    this.#hemiLight = hemi;

    const key = new THREE.DirectionalLight(0xffffff, LIGHT_BRIGHT.key);
    key.position.copy(KEY_LIGHT_POSITION);
    this.#scene.add(key);
    this.#keyLight = key;

    const fill = new THREE.DirectionalLight(0xb8d0ff, LIGHT_BRIGHT.fill);
    fill.position.set(-2.5, 1, 2);
    this.#scene.add(fill);
    this.#fillLight = fill;

    const rim = new THREE.DirectionalLight(0xffd9b0, LIGHT_BRIGHT.rim);
    rim.position.set(0, 1.5, -3);
    this.#scene.add(rim);
    this.#rimLight = rim;

    this.#setupBloom();

    this.#controls = new OrbitControls(this.#camera, canvas);
    this.#controls.enableDamping = true;
    this.#controls.enablePan = false;
    this.#controls.enableZoom = false;

    // OrbitControls' constructor sets inline `touch-action: none` on the canvas,
    // beating our `pan-y` CSS rule. Reset so vertical swipes scroll the page
    // and OrbitControls only receives horizontals.
    canvas.style.touchAction = 'pan-y';

    this.#installMobilePolarLock();

    this.#controls.autoRotate = this.dataset.autoRotate !== 'false';
    this.#defaultAutoRotate = this.#controls.autoRotate;
    this.#controls.autoRotateSpeed = AUTO_ROTATE_SPEED;
    // The controls instance survives Horizon's morph but the host element is
    // replaced. Stash a host pointer so the start listener writes to the
    // live element, not a detached node.
    this.#controls._bunnyHintHost = this;
    this.#controls.addEventListener('start', () => {
      this.#controls.autoRotate = false;
      this.#controls._bunnyHintDismissed = true;
      const host = this.#controls._bunnyHintHost;
      if (host?.isConnected) host.dataset.rotateHint = 'off';
    });

    this.#startedAt = performance.now();
    this.#resize();

    // Multi-product hosts defer inactive panels so 4 viewers don't race for
    // 8MB of GLBs at cold start. resume() fires the load on first activation.
    if (this.dataset.deferLoad === 'true') {
      this.#deferredLoadPending = true;
      this.dataset.state = 'pending';
    } else {
      this.#loadModel();
    }

    this.#bindHostListeners();
    this.#bindPointerListeners();
    this.#bindSodaCanToggle();

    this.#tick();
  }

  // Halo-vars target. Picker / viewer write `--bunny-halo-*` here so the
  // CSS pseudo-element halo lights up. Per-panel scope in the configurator
  // (one halo per panel); section wrapper on PDP / hero; documentElement
  // as last-resort fallback.
  #haloHost() {
    if (this.#hostPanel) return this.#hostPanel;
    if (this.#scopeRoot?.style) return this.#scopeRoot;
    return document.documentElement;
  }

  // Stand up the bloom-pipeline helper refs that #setupBloom and
  // #adoptCachedState both need. The MeshBasicMaterial + Layers + bound
  // callbacks are per-instance (callbacks capture `this`), so the adopt
  // path re-creates them rather than caching across morph.
  #initBloomPipelineRefs() {
    this.#darkMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    this.#bloomLayerCheck = new THREE.Layers();
    this.#bloomLayerCheck.set(BLOOM_LAYER);
    this.#darkenBound = (obj) => this.#darkenForBloom(obj);
    this.#restoreBound = (obj) => this.#restoreFromBloom(obj);
  }

  #recomputeAnyGlow() {
    for (const entry of this.#surfaces.values()) {
      if (entry.isGlow) { this.#anyGlowCached = true; return; }
    }
    this.#anyGlowCached = false;
  }

  // Section-scoped events + ResizeObserver + off-screen pause. Idempotent —
  // unbinds first so a duplicate bind doesn't stack listeners. Despite
  // the historical "picker" name, this is the umbrella host-event bootstrap.
  #bindHostListeners() {
    this.#unbindHostListeners();

    this.#onSwatchChangeBound = (event) => this.#onSwatchChange(event);
    this.#scopeRoot.addEventListener('bunny:swatch-change', this.#onSwatchChangeBound);

    this.#onRarityChangeBound = (event) => this.#onRarityChange(event);
    this.#scopeRoot.addEventListener('bunny:rarity-change', this.#onRarityChangeBound);

    this.#onVariantUpdateBound = (event) => this.#onVariantUpdate(event);
    this.#scopeRoot.addEventListener('variant:update', this.#onVariantUpdateBound);

    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (!entry.isIntersecting) {
        // Don't touch #paused if host already paused us (inactive tab) —
        // #offScreenPaused stays false so we won't auto-resume on scroll-back.
        if (!this.#paused) {
          this.#offScreenPaused = true;
          this.#paused = true;
          cancelAnimationFrame(this.#rafId);
          this.#rafId = 0;
        }
      } else if (this.#offScreenPaused) {
        this.#offScreenPaused = false;
        this.#paused = false;
        this.#tick();
      }
    });
    this.#intersectionObserver.observe(this);
  }

  #unbindHostListeners() {
    const scope = this.#scopeRoot || document;
    if (this.#onSwatchChangeBound) {
      scope.removeEventListener('bunny:swatch-change', this.#onSwatchChangeBound);
      this.#onSwatchChangeBound = null;
    }
    if (this.#onRarityChangeBound) {
      scope.removeEventListener('bunny:rarity-change', this.#onRarityChangeBound);
      this.#onRarityChangeBound = null;
    }
    if (this.#onVariantUpdateBound) {
      scope.removeEventListener('variant:update', this.#onVariantUpdateBound);
      this.#onVariantUpdateBound = null;
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();

    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
    this.#unbindHostListeners();
    if (this.#mobileMediaQuery && this.#onMobileMediaChangeBound) {
      this.#mobileMediaQuery.removeEventListener('change', this.#onMobileMediaChangeBound);
      this.#mobileMediaQuery = null;
      this.#onMobileMediaChangeBound = null;
    }
    this.#unbindPointerListeners();
    this.#unbindSodaCanToggle();

    // Stash WebGL state for the next mount on this media id. Skip caching
    // for never-loaded deferred viewers and in-flight loads — caching a
    // partial scene would let #adoptCachedState skip #loadModel and the
    // new mount would adopt an empty pipeline. (See #onModelLoaded for
    // the matching `!isConnected` bail.)
    const cacheKey = this.#cacheKey;
    const neverLoaded = this.#deferredLoadPending && !this.#modelLoadStarted;
    const loadInFlight = this.#modelLoadStarted && !this.#modelLoadCompleted;
    const skipCache = neverLoaded || loadInFlight;
    if (this.#ownsMediaId && cacheKey && this.#renderer && !skipCache) {
      if (Bunny3dViewer.#stateCache.size >= Bunny3dViewer.#STATE_CACHE_MAX) {
        const oldestKey = Bunny3dViewer.#stateCache.keys().next().value;
        const oldest = Bunny3dViewer.#stateCache.get(oldestKey);
        Bunny3dViewer.#stateCache.delete(oldestKey);
        Bunny3dViewer.#disposeCachedState(oldest);
      }
      Bunny3dViewer.#stateCache.set(cacheKey, {
        canvas: this.#renderer.domElement,
        renderer: this.#renderer,
        scene: this.#scene,
        camera: this.#camera,
        controls: this.#controls,
        surfaces: this.#surfaces,
        startedAt: this.#startedAt,
        defaultAutoRotate: this.#defaultAutoRotate,
        bloomComposer: this.#bloomComposer,
        finalComposer: this.#finalComposer,
        hemiLight: this.#hemiLight,
        keyLight: this.#keyLight,
        fillLight: this.#fillLight,
        rimLight: this.#rimLight,
        dimCurrent: this.#dimCurrent,
        dimTarget: this.#dimTarget,
        lastRarityCost: this.#lastRarityCost,
        sodaCanMode: this.#sodaCanMode,
        sodaCanRoot: this.#sodaCanRoot,
        sodaCanWidthWorld: this.#sodaCanWidthWorld,
        toyRoot: this.#toyRoot,
        toyCenteredX: this.#toyCenteredX,
        toyHeightWorld: this.#toyHeightWorld,
        toyWidthWorld: this.#toyWidthWorld,
        toyLengthIn: this.#toyLengthIn,
      });
      Bunny3dViewer.#activeMediaIds.delete(cacheKey);
      this.#ownsMediaId = false;
      return;
    }

    // Real teardown — clear background overrides and halo vars so the next
    // viewer starts fresh. Release the activeMediaIds slot so a re-mount on
    // the same key isn't misdiagnosed as a duplicate.
    if (skipCache && cacheKey && this.#ownsMediaId) {
      Bunny3dViewer.#activeMediaIds.delete(cacheKey);
      this.#ownsMediaId = false;
    }
    this.#sectionEl?.style.removeProperty('--color-background');
    this.#productInfoEl?.style.removeProperty('--color-background');
    // Clear the EXACT host we wrote to in our last #applyRarity. Falling
    // back to #haloHost() would risk wiping vars set by a sibling viewer
    // when both fell back to documentElement.
    const haloHost = this.#lastHaloHost || this.#haloHost();
    haloHost.style.removeProperty('--bunny-halo-glow-mod');
    haloHost.style.removeProperty('--bunny-halo-rgb');
    haloHost.style.removeProperty('--bunny-halo-opacity');
    this.#lastHaloHost = null;

    const disposed = Bunny3dViewer.#dedupedMaterials(this.#surfaces.values());
    for (const material of disposed) material.dispose();
    // Soda Can scene is parented to #scene, not in #surfaces — dispose
    // separately. Uses Three's default materials, so a plain dispose covers it.
    Bunny3dViewer.#disposeSodaCanScene(this.#sodaCanRoot, disposed);
    this.#renderer?.dispose();
  }

  // Extracted from connectedCallback so resume() can fire it on the first
  // activation of a deferred viewer.
  #loadModel() {
    const modelUrl = this.dataset.modelUrl;
    if (!modelUrl) {
      console.warn('[bunny-3d-viewer] no data-model-url attribute set');
      return;
    }
    // resume() clears #deferredLoadPending before calling — check the host
    // attr to detect deferred viewers (re-arm flags for resume() retry).
    const isDeferredViewer = this.dataset.deferLoad === 'true';
    this.#modelLoadStarted = true;
    this.dataset.state = 'loading';
    const loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath(DRACO_DECODER_PATH);
    loader.setDRACOLoader(draco);
    loader.loadAsync(modelUrl).then(
      (gltf) => this.#onModelLoaded(gltf),
      (err) => {
        console.warn('[bunny-3d-viewer] model load failed', err);
        this.dataset.state = 'error';
        // Re-arm for retry on resume() (deferred viewers only — immediate
        // mounts have no resume-from-error path).
        if (isDeferredViewer) {
          this.#modelLoadStarted = false;
          this.#deferredLoadPending = true;
        }
      }
    );
  }

  // Pause / resume the rAF loop for multi-viewer hosts (pour-configurator
  // tabs). Materials and listeners stay live so resume picks up instantly.
  pause() {
    // Explicit host pause supersedes off-screen auto-pause.
    this.#offScreenPaused = false;
    if (this.#paused) return;
    this.#paused = true;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
  }

  resume() {
    // First activation of a deferred viewer fires the GLB load.
    if (this.#deferredLoadPending) {
      this.#deferredLoadPending = false;
      this.#loadModel();
    }
    this.#offScreenPaused = false;
    if (!this.#paused) return;
    this.#paused = false;
    this.#tick();
  }

  // Adopt a cached pipeline into a freshly-mounted element after Horizon's
  // morph. Replaces the new canvas placeholder with the live cached canvas
  // so rendering never pauses; re-binds listeners; restarts rAF.
  #adoptCachedState(cached) {
    // Disable entrance transitions on first paint via CSS [data-adopting]
    // — otherwise the halo / rotate hint / soda-can toggle all play their
    // change-state transitions and read as a flicker. Cleared next frame.
    this.dataset.adopting = 'true';
    this.#renderer = cached.renderer;
    this.#scene = cached.scene;
    this.#camera = cached.camera;
    this.#controls = cached.controls;
    this.#surfaces = cached.surfaces ?? new Map();
    // Cached surfaces may still carry isGlow=true on regions that were Glow
    // before the morph; sync the memo so #anySurfaceGlow returns truth.
    this.#recomputeAnyGlow();
    this.#startedAt = cached.startedAt;
    this.#defaultAutoRotate = cached.defaultAutoRotate ?? true;
    // Rotation-hint state lives on the controls (it survives morph). Refresh
    // the host pointer so the start listener writes to this fresh element,
    // and carry the dismissed flag onto the new host so the hint doesn't
    // flash back in if the user had already dragged before the morph.
    this.#controls._bunnyHintHost = this;
    if (this.#controls._bunnyHintDismissed) {
      this.dataset.rotateHint = 'off';
    }
    this.#bloomComposer = cached.bloomComposer ?? null;
    this.#finalComposer = cached.finalComposer ?? null;
    // Bound traversal helpers capture `this`, so recreate on the new instance.
    // The MeshBasicMaterial + Layers are cheaper to re-allocate than to cache.
    this.#initBloomPipelineRefs();
    this.#savedMaterials = new Map();
    this.#hemiLight = cached.hemiLight ?? null;
    this.#keyLight = cached.keyLight ?? null;
    this.#fillLight = cached.fillLight ?? null;
    this.#rimLight = cached.rimLight ?? null;
    this.#dimCurrent = cached.dimCurrent ?? 0;
    this.#dimTarget = cached.dimTarget ?? 0;
    // Re-apply rarity BEFORE anything else — the morph stripped the inline
    // halo vars from the section wrapper. Writing them synchronously here,
    // pre-paint, avoids the fade-out + back-in the opacity transition would
    // otherwise animate.
    this.#lastRarityCost = cached.lastRarityCost ?? null;
    if (this.#lastRarityCost !== null) {
      this.#applyRarity(this.#lastRarityCost);
    }
    this.#sectionEl = null;
    this.#productInfoEl = null;
    this.#updateSectionForeground();

    const placeholderCanvas = this.querySelector('canvas');
    if (placeholderCanvas && placeholderCanvas !== cached.canvas) {
      placeholderCanvas.replaceWith(cached.canvas);
    }

    // #hostPanel re-resolves on the fresh element since the morph swapped it.
    this.#hostPanel = this.closest('[data-pour-panel]');
    this.#bindHostListeners();
    this.#resize();

    this.#installMobilePolarLock();

    this.#bindPointerListeners();

    // Soda Can: can root + toy root survived the morph in the cached scene
    // graph. Restore bounds + length-in so #applySodaCanLayout can recompute
    // wu/in against the new variant's `total_length_in`.
    this.#sodaCanMode = cached.sodaCanMode ?? false;
    this.#sodaCanRoot = cached.sodaCanRoot ?? null;
    this.#sodaCanWidthWorld = cached.sodaCanWidthWorld ?? 0;
    this.#toyRoot = cached.toyRoot ?? null;
    this.#toyCenteredX = cached.toyCenteredX ?? 0;
    this.#toyHeightWorld = cached.toyHeightWorld ?? 0;
    this.#toyWidthWorld = cached.toyWidthWorld ?? 0;
    this.#toyLengthIn = this.#readToyLengthInches();
    this.#bindSodaCanToggle();
    if (this.#sodaCanMode) {
      // Tween toward new targets so a Size change reads as a smooth resize
      // (idempotent if length didn't change).
      this.#applySodaCanLayout(true, true);
    }

    this.dataset.state = 'ready';
    this.#hydrateFromPicker();
    this.#tick();
    // Double-rAF: first frame commits adopted styles, second clears the
    // adopting flag. Single rAF would let the clear land in the same
    // style recalc and still fire transitions.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.isConnected) delete this.dataset.adopting;
      });
    });
  }

  // Mobile (≤749px): pin polar to π/2 so even diagonal swipes can only
  // azimuth-rotate. Reactive on viewport resize / orientation change.
  #installMobilePolarLock() {
    if (!this.#controls) return;
    if (this.#mobileMediaQuery && this.#onMobileMediaChangeBound) {
      this.#mobileMediaQuery.removeEventListener('change', this.#onMobileMediaChangeBound);
    }
    const mq = window.matchMedia('(max-width: 749px)');
    const apply = () => {
      if (!this.#controls) return;
      if (mq.matches) {
        this.#controls.minPolarAngle = Math.PI / 2;
        this.#controls.maxPolarAngle = Math.PI / 2;
      } else {
        this.#controls.minPolarAngle = 0;
        this.#controls.maxPolarAngle = Math.PI;
      }
    };
    apply();
    this.#mobileMediaQuery = mq;
    this.#onMobileMediaChangeBound = apply;
    mq.addEventListener('change', apply);
  }

  #resize() {
    if (!this.#renderer || !this.#camera) return;
    const w = this.clientWidth || 1;
    const h = this.clientHeight || 1;
    this.#renderer.setSize(w, h, false);
    this.#camera.aspect = w / h;
    this.#camera.updateProjectionMatrix();
    this.#bloomComposer?.setSize(w, h);
    this.#finalComposer?.setSize(w, h);
    // Render synchronously inside the RO callback so the new buffer commits
    // before paint — otherwise the canvas displays the old buffer stretched
    // by `width/height: 100% !important` for one frame (flickers during
    // mobile dvh animations where RO fires every few ms).
    this.#renderFrame();
  }

  #onModelLoaded(gltf) {
    // Bail if disconnected mid-load — the new mount has already fired its
    // own #loadModel; mutating this stale gltf would corrupt the new scene.
    if (!this.isConnected) return;
    const root = gltf.scene;
    // Rotate before bbox so auto-fit accounts for the rotated silhouette.
    root.rotation.y = INITIAL_MODEL_Y_ROTATION;
    root.updateMatrixWorld(true);
    const bbox = new THREE.Box3().setFromObject(root);
    const tip = bbox.max.y;
    const base = bbox.min.y;
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());

    // Normalize: Nomad exports at ~250 units, far past the camera's far
    // plane. Fit longest axis to 2 world units centered at origin.
    const longest = Math.max(size.x, size.y, size.z) || 1;
    const TARGET_SIZE = 2;
    const scale = TARGET_SIZE / longest;
    root.scale.setScalar(scale);
    root.position.set(-center.x * scale, -center.y * scale, -center.z * scale);

    // Capture world-space toy bounds for Soda Can mode (wu/in baseline).
    this.#toyRoot = root;
    this.#toyCenteredX = -center.x * scale;
    this.#toyHeightWorld = size.y * scale;
    this.#toyWidthWorld = size.x * scale;
    this.#toyLengthIn = this.#readToyLengthInches();

    this.#camera.position.set(0, 0, CAMERA_DISTANCE_SOLO);
    this.#controls.target.set(0, 0, 0);
    this.#controls.update();

    const meshes = [];
    root.traverse((child) => {
      if (child.isMesh) meshes.push(child);
    });

    // Nomad exports GLBs without NORMAL attribute — derive smooth normals
    // so PBR has something to light. (No-op if normals are present.)
    for (const mesh of meshes) {
      const geom = mesh.geometry;
      if (geom && !geom.attributes.normal) geom.computeVertexNormals();
    }

    // data-surfaces = CSV of the variant's split-surface handles, emitted
    // from product-media.liquid. Order matters: list longer handles first
    // (e.g. "shaft-ring" before "shaft") so prefix-match doesn't shadow.
    const surfaceNamesRaw = this.dataset.surfaces || 'shaft,base';
    const surfaceNames = surfaceNamesRaw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    // Nomad splits each surface into many sub-meshes (Shaft_1..Shaft_11,
    // Base_1..Base_12, etc.) — bucket by case-insensitive name prefix and
    // share one material across the group. First-match wins.
    const bucketed = new Map();
    for (const name of surfaceNames) bucketed.set(name, []);
    for (const mesh of meshes) {
      const lc = (mesh.name || '').toLowerCase();
      for (const name of surfaceNames) {
        if (lc.startsWith(name)) {
          bucketed.get(name).push(mesh);
          break;
        }
      }
    }

    // Region-local bounds drive Split Mode's Faded gradient; union bounds
    // drive Entire Mode so the gradient spans seamlessly across surfaces.
    const unionBounds = { tip, base };
    this.#surfaces = new Map();
    for (const [name, surfaceMeshes] of bucketed) {
      if (!surfaceMeshes.length) continue;
      const box = new THREE.Box3();
      for (const m of surfaceMeshes) box.expandByObject(m);
      const bounds = {
        regionTip: box.max.y,
        regionBase: box.min.y,
        unionTip: unionBounds.tip,
        unionBase: unionBounds.base,
      };
      const material = this.#createMaterial(bounds);
      for (const mesh of surfaceMeshes) mesh.material = material;
      this.#surfaces.set(name, { material, meshes: surfaceMeshes, bounds, isGlow: false });
    }

    // Last-resort fallback: no prefix matched. Alias one material across
    // every declared surface so any region pick paints; attach the actual
    // mesh list only to the first entry so #updateBloomLayers doesn't
    // toggle the same meshes N times.
    if (this.#surfaces.size === 0 && meshes.length >= 1) {
      console.warn('[bunny-3d-viewer] falling back to single-region paint mode');
      const bounds = {
        regionTip: unionBounds.tip,
        regionBase: unionBounds.base,
        unionTip: unionBounds.tip,
        unionBase: unionBounds.base,
      };
      const material = this.#createMaterial(bounds);
      for (const mesh of meshes) mesh.material = material;
      const fallbackNames = surfaceNames.length ? surfaceNames : ['shaft', 'base'];
      let first = true;
      for (const name of fallbackNames) {
        this.#surfaces.set(name, {
          material,
          meshes: first ? meshes : [],
          bounds,
          isGlow: false,
        });
        first = false;
      }
    }

    this.#scene.add(root);
    this.dataset.state = 'ready';
    // All freshly-built entries have isGlow: false; sync the cache before
    // any paint asks #anySurfaceGlow.
    this.#recomputeAnyGlow();
    this.#hydrateFromPicker();
    // Soda Can may have been toggled on before the GLB finished — apply
    // now (firstTimeSize handling pre-positions so it doesn't slide from origin).
    if (this.#sodaCanMode) this.#applySodaCanLayout(true, true);
    this.#modelLoadCompleted = true;
  }

  #createMaterial(bounds) {
    // Silicone PBR: low roughness, no metal, light clearcoat for the wet
    // surface response. Color is per-fragment via onBeforeCompile.
    const material = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.42,
      metalness: 0.0,
      clearcoat: 0.2,
      clearcoatRoughness: 0.6,
      sheen: 0.0,
      envMapIntensity: 0.85,
      side: THREE.DoubleSide,
    });

    // Uniforms on userData so they're reachable from outside the compile
    // callback. References (not copies) are spliced into shader.uniforms.
    // Bounds let #paintMaterial swap uTip/uBase region↔union at paint time.
    material.userData.bounds = bounds;
    material.userData.uniforms = {
      uSwatchType: { value: 0 },
      uColor1: { value: new THREE.Color('#cccccc') },
      uColor2: { value: new THREE.Color('#cccccc') },
      uColorMid: { value: new THREE.Color('#cccccc') },
      uHasMid: { value: 0 },
      uTime: { value: 0 },
      uTip: { value: bounds.regionTip },
      uBase: { value: bounds.regionBase },
      uModelHeight: { value: bounds.unionTip - bounds.unionBase },
      uGlowBase: { value: GLOW_BASE },
      uGlowAmp: { value: GLOW_AMP },
      uSparkle: { value: 0 },
      // Mirrors the key DirectionalLight position via KEY_LIGHT_POSITION.
      uKeyLightDir: { value: KEY_LIGHT_POSITION.clone().normalize() },
    };

    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, material.userData.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', VERT_DECLS)
        .replace('#include <begin_vertex>', VERT_BODY);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', FRAG_DECLS)
        .replace('#include <color_fragment>', FRAG_COLOR)
        .replace('#include <emissivemap_fragment>', FRAG_EMISSIVE);
    };

    return material;
  }

  // Multi-panel isolation in the pour configurator: reject events emitted
  // by a sibling panel's picker / premade-paint. No-op on PDP and hero.
  #originMatches(event) {
    if (!this.#hostPanel) return true;
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return true;
    return target.closest('[data-pour-panel]') === this.#hostPanel;
  }

  #onSwatchChange(event) {
    if (!this.#originMatches(event)) return;
    const detail = event.detail;
    if (!detail || !detail.swatch) return;
    this.#applySelection(detail.region, detail.mode, detail.swatch);
  }

  #onRarityChange(event) {
    if (!this.#originMatches(event)) return;
    const detail = event.detail;
    if (!detail) return;
    this.#applyRarity(detail.cost);
  }

  #applyRarity(cost) {
    // Halo vars live on the panel (configurator) or section wrapper (PDP /
    // hero) — host-scoped would vanish on the morph. Per-panel scope keeps
    // each configurator panel's halo bound to its own picker.
    this.#lastRarityCost = cost;
    const root = this.#haloHost();
    this.#lastHaloHost = root;

    // Prefer the `rarity_tier` metaobject tokens emitted by bunny-tokens.liquid
    // — Sierra can re-tint from Shopify Admin without redeploying. Falls
    // through to RARITY_CONFIG (canonical hex/intensity ladder duplicated in
    // JS as a partial-deploy safety net) if the tokens snippet didn't load.
    const tokens = getComputedStyle(document.documentElement);
    const tokenRgb = tokens.getPropertyValue(`--rarity-${cost}-rgb`).trim();
    const tokenOpacity = parseFloat(tokens.getPropertyValue(`--rarity-${cost}-opacity`));
    if (tokenRgb && Number.isFinite(tokenOpacity)) {
      root.style.setProperty('--bunny-halo-rgb', tokenRgb);
      root.style.setProperty('--bunny-halo-opacity', String(tokenOpacity));
      return;
    }

    const config = RARITY_CONFIG[cost];
    if (!config) {
      console.warn(`[bunny-3d-viewer] #applyRarity: no config for cost ${cost}`);
      root.style.setProperty('--bunny-halo-opacity', '0');
      return;
    }
    // Manual hex parse — THREE.Color stores linear and would darken the CSS color.
    const r = (config.hex >> 16) & 0xff;
    const g = (config.hex >> 8) & 0xff;
    const b = config.hex & 0xff;
    root.style.setProperty('--bunny-halo-rgb', `${r} ${g} ${b}`);
    root.style.setProperty('--bunny-halo-opacity', String(config.intensity));
  }

  // Catch up on picker / premade / rotator state in case their emits landed
  // before this viewer's listener was bound (cold load with ?variant= in URL).
  #hydrateFromPicker() {
    // Scope to the host panel in the configurator — a section-wide query
    // would mirror sibling panels' pressed chips into this viewer (their
    // Model chip would arrive as region: 'model' and entire-mode-paint,
    // clobbering this panel's split picks).
    const scope = this.#hostPanel || this.#scopeRoot || document;

    const pourStyleValue = scope.querySelector('.swatch-picker__pour-style-value[data-rarity]');
    if (pourStyleValue) {
      const cost = parseInt(pourStyleValue.dataset.rarity, 10);
      if (Number.isFinite(cost)) this.#applyRarity(cost);
    }

    const pressed = scope.querySelectorAll('swatch-picker .swatch-chip[aria-pressed="true"]');
    if (pressed.length > 0) {
      for (const chip of pressed) {
        const region = chip.dataset.position;
        if (!region) continue;
        const mode = region === 'model' ? 'entire' : 'split';
        // Sparkle isn't on the chip dataset — read live state from the chip's
        // own picker (handles multiple pickers per page).
        const picker = chip.closest('swatch-picker');
        const sparkleCb = picker?.querySelector(
          `.swatch-picker__sparkle-input[data-position="${region}"]`
        );
        this.#applySelection(region, mode, {
          type: chip.dataset.swatchType,
          color: chip.dataset.color,
          color2: chip.dataset.color2,
          colorMid: chip.dataset.colorMid,
          sparkle: sparkleCb?.checked === true,
        });
      }
      return;
    }

    const premade = scope.querySelector('bunny-premade-paint');
    if (premade && typeof premade.replayCurrent === 'function') {
      premade.replayCurrent();
      return;
    }

    // Hero rotator's first tick often beats GLB load — that dispatch lands
    // on null materials and gets dropped. Ask it to re-emit now.
    const rotator = scope.querySelector('bunny-hero-rotator');
    if (rotator && typeof rotator.replayCurrent === 'function') {
      rotator.replayCurrent();
    }
  }

  #applySelection(region, mode, swatch) {
    // Entire Mode (or a Model-region pick) paints every surface with the
    // same swatch + union bounds — band crosses every seam continuously.
    // Split Mode paints just the matching surface with region-local bounds.
    //
    // `model` is the synthetic union surface; it is never an entry in
    // #surfaces. A model-region pick falls through the union branch.
    const useUnion = mode === 'entire' || region === 'model';
    const isGlow = swatch && swatch.type === 'glow';
    if (useUnion) {
      // Dedupe so the single-region fallback doesn't repaint the same
      // material N times.
      for (const material of Bunny3dViewer.#dedupedMaterials(this.#surfaces.values())) {
        this.#paintMaterial(material, swatch, true);
      }
      for (const entry of this.#surfaces.values()) {
        entry.isGlow = isGlow;
      }
    } else {
      const entry = this.#surfaces.get(region);
      if (entry) {
        this.#paintMaterial(entry.material, swatch, false);
        entry.isGlow = isGlow;
      }
    }
    this.#recomputeAnyGlow();
    this.#updateBloomLayers();
    this.#updateSectionForeground();
    this.#dimTarget = this.#anyGlowCached ? 1 : 0;
  }

  // Memoized; recomputed by #recomputeAnyGlow on the few sites that mutate
  // entry.isGlow or replace #surfaces wholesale (#applySelection,
  // #adoptCachedState, #onModelLoaded).
  #anySurfaceGlow() {
    return this.#anyGlowCached;
  }

  // Time-constant ease toward #dimTarget — fast start, gentle finish, no
  // overshoot. Matches the .section-background CSS fade feel.
  #stepDim(dt) {
    if (this.#dimCurrent === this.#dimTarget) return;
    const ease = 1 - Math.exp(-dt / DIM_TIME_CONSTANT_S);
    this.#dimCurrent += (this.#dimTarget - this.#dimCurrent) * ease;
    if (Math.abs(this.#dimTarget - this.#dimCurrent) < 0.0005) {
      this.#dimCurrent = this.#dimTarget;
    }
    const t = this.#dimCurrent;
    const lerp = (a, b) => a + (b - a) * t;

    if (this.#hemiLight) this.#hemiLight.intensity = lerp(LIGHT_BRIGHT.hemi, LIGHT_DIM.hemi);
    if (this.#keyLight) this.#keyLight.intensity = lerp(LIGHT_BRIGHT.key, LIGHT_DIM.key);
    if (this.#fillLight) this.#fillLight.intensity = lerp(LIGHT_BRIGHT.fill, LIGHT_DIM.fill);
    if (this.#rimLight) this.#rimLight.intensity = lerp(LIGHT_BRIGHT.rim, LIGHT_DIM.rim);

    const env = lerp(LIGHT_BRIGHT.envMap, LIGHT_DIM.envMap);
    for (const material of Bunny3dViewer.#dedupedMaterials(this.#surfaces.values())) {
      material.envMapIntensity = env;
    }

    if (this.#renderer) this.#renderer.toneMappingExposure = lerp(LIGHT_BRIGHT.exposure, LIGHT_DIM.exposure);
  }

  // While any region is Glow, override --color-background on the section
  // wrapper + .product-information so consumers inherit "lights off in here".
  // Both wrappers sit above the gallery morph boundary, so inline writes
  // persist across variant:update. --bunny-halo-glow-mod scales the halo
  // opacity (CSS-side) and lives alongside the halo vars (panel scope so
  // one Glow pick doesn't dim every configurator panel's halo).
  #updateSectionForeground() {
    if (!this.#productInfoEl) {
      this.#productInfoEl = this.closest('.product-information');
    }
    if (!this.#sectionEl) {
      const sibling = this.#productInfoEl?.parentElement?.querySelector(':scope > .section-background');
      if (sibling) this.#sectionEl = sibling;
    }
    const targets = [this.#sectionEl, this.#productInfoEl].filter(Boolean);
    const glowOn = this.#anySurfaceGlow();
    const haloHost = this.#haloHost();
    if (glowOn) {
      for (const el of targets) el.style.setProperty('--color-background', '#000000');
      haloHost.style.setProperty('--bunny-halo-glow-mod', '0.55');
    } else {
      for (const el of targets) el.style.removeProperty('--color-background');
      haloHost.style.removeProperty('--bunny-halo-glow-mod');
    }
  }

  #updateBloomLayers() {
    // NOTE: in the single-region fallback path, every #surfaces entry shares
    // a material but only the first entry's `meshes` is non-empty (others
    // are intentionally []). That's why we iterate per-entry meshes rather
    // than per-material — duplicate entries would re-toggle the same meshes.
    for (const entry of this.#surfaces.values()) {
      for (const m of entry.meshes) {
        if (entry.isGlow) m.layers.enable(BLOOM_LAYER);
        else m.layers.disable(BLOOM_LAYER);
      }
    }
  }

  // GOTCHA: only mutate `.value` on existing uniform wrappers — never
  // reassign `u.uXxx = { value: … }`. onBeforeCompile splices these wrappers
  // by reference into shader.uniforms (see #createMaterial); reassigning
  // detaches the GPU-bound uniform from our mutations.
  #paintMaterial(material, swatch, useUnion) {
    if (!material) return;
    const u = material.userData.uniforms;
    const b = material.userData.bounds;
    if (!u || !b) return;
    u.uSwatchType.value = SWATCH_TYPE[swatch.type] ?? 0;
    if (swatch.color) u.uColor1.value.set(swatch.color);
    u.uColor2.value.set(swatch.color2 || swatch.color || '#cccccc');
    u.uColorMid.value.set(swatch.colorMid || swatch.color2 || swatch.color || '#cccccc');
    u.uHasMid.value = swatch.colorMid ? 1.0 : 0.0;
    u.uTip.value = useUnion ? b.unionTip : b.regionTip;
    u.uBase.value = useUnion ? b.unionBase : b.regionBase;
    u.uSparkle.value = swatch.sparkle ? 1.0 : 0.0;
  }

  // Two-pass selective bloom:
  //   bloomComposer: full scene → off-screen RT, non-glow meshes painted
  //     black via material swap (depth occludes correctly).
  //   finalComposer: full scene + mix ShaderPass that adds the bloom RT.
  #setupBloom() {
    const w = Math.max(this.clientWidth || 1, 1);
    const h = Math.max(this.clientHeight || 1, 1);

    this.#initBloomPipelineRefs();

    const bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD);
    this.#bloomComposer = new EffectComposer(this.#renderer);
    this.#bloomComposer.renderToScreen = false;
    this.#bloomComposer.addPass(new RenderPass(this.#scene, this.#camera));
    this.#bloomComposer.addPass(bloomPass);

    // Mix shader: sum scene RGB + bloom RT, preserve base alpha. Alpha guard
    // keeps the canvas transparent so the CSS rarity halo shows through.
    const mixMaterial = new THREE.ShaderMaterial({
      uniforms: {
        baseTexture: { value: null },
        bloomTexture: { value: this.#bloomComposer.renderTarget2.texture },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D baseTexture;
        uniform sampler2D bloomTexture;
        varying vec2 vUv;
        // Composer RTs are linear; without this, ShaderPass would write
        // linear values to the sRGB canvas and the whole image reads washed
        // out (renderer.render has <colorspace_fragment> applied automatically).
        vec3 linearToSRGB(vec3 v) {
          bvec3 cutoff = lessThanEqual(v, vec3(0.0031308));
          vec3 higher = pow(v, vec3(1.0 / 2.4)) * 1.055 - vec3(0.055);
          vec3 lower = v * 12.92;
          return mix(higher, lower, vec3(cutoff));
        }
        void main() {
          vec4 base = texture2D(baseTexture, vUv);
          vec3 bloom = texture2D(bloomTexture, vUv).rgb;
          vec3 linear = base.rgb + bloom;
          gl_FragColor = vec4(linearToSRGB(linear), base.a);
        }
      `,
      defines: {},
      transparent: true,
    });
    const mixPass = new ShaderPass(mixMaterial, 'baseTexture');
    mixPass.needsSwap = true;

    this.#finalComposer = new EffectComposer(this.#renderer);
    this.#finalComposer.addPass(new RenderPass(this.#scene, this.#camera));
    this.#finalComposer.addPass(mixPass);
  }

  // No glow + dim relaxed → fall through to single-call render for
  // status-quo perf. Otherwise run the selective-bloom pipeline so lights
  // animate smoothly as the bloom contribution disappears.
  #renderFrame() {
    if (!this.#renderer) return;
    const bloomActive = this.#anySurfaceGlow() || this.#dimCurrent > 0.0005;
    if (!bloomActive) {
      this.#renderer.render(this.#scene, this.#camera);
      return;
    }
    this.#scene.traverse(this.#darkenBound);
    this.#bloomComposer.render();
    this.#scene.traverse(this.#restoreBound);
    this.#finalComposer.render();
  }

  #darkenForBloom(obj) {
    if (obj.isMesh && !this.#bloomLayerCheck.test(obj.layers)) {
      this.#savedMaterials.set(obj.uuid, obj.material);
      obj.material = this.#darkMaterial;
    }
  }

  #restoreFromBloom(obj) {
    const saved = this.#savedMaterials.get(obj.uuid);
    if (saved) {
      obj.material = saved;
      this.#savedMaterials.delete(obj.uuid);
    }
  }

  // rAF loop while connected + not paused + on-screen. IntersectionObserver
  // auto-pauses off-screen. No early-exit on "nothing animating" because all
  // procedural shaders advance uTime every frame and tweens depend on it.
  #tick = () => {
    if (this.#paused) return;
    const now = performance.now();
    const t = (now - this.#startedAt) / 1000;
    const dt = this.#lastTickAt ? Math.min(0.1, (now - this.#lastTickAt) / 1000) : 0;
    this.#lastTickAt = now;
    const seenTime = new Set();
    for (const entry of this.#surfaces.values()) {
      const u = entry.material?.userData.uniforms;
      if (!u) continue;
      if (seenTime.has(entry.material)) continue;
      u.uTime.value = t;
      seenTime.add(entry.material);
    }
    // Pass dt so auto-rotate scales with wall-clock seconds, not frames.
    // Without this, throttled rAF (battery saver, slow GPU) halves visible spin.
    this.#controls?.update(dt);
    if (this.#returning) this.#stepReturn(now);
    if (this.#toggleTweenStart) this.#stepToggleTween(now);
    if (dt > 0) this.#stepDim(dt);
    this.#renderFrame();
    this.#rafId = requestAnimationFrame(this.#tick);
  };

  // On pointer-leave: ease phi back to upright (preserve user's azimuth),
  // restore auto-rotate. Skip the tween if phi is already upright.
  #bindPointerListeners() {
    this.#pointerLeaveBound = () => this.#startReturn();
    this.addEventListener('pointerleave', this.#pointerLeaveBound);
  }

  #unbindPointerListeners() {
    if (this.#pointerLeaveBound) {
      this.removeEventListener('pointerleave', this.#pointerLeaveBound);
      this.#pointerLeaveBound = null;
    }
  }

  #startReturn() {
    if (!this.#controls || !this.#camera) return;
    // autoRotate is still true → user hasn't dragged yet, nothing to ease.
    if (this.#controls.autoRotate) return;
    // Resume auto-rotate immediately. The phi tween runs in parallel with
    // theta increment — #stepReturn re-derives spherical each frame so
    // theta/radius are preserved, only phi is overridden.
    this.#controls.autoRotate = this.#defaultAutoRotate;
    if (!this.#returnSphericalScratch) this.#returnSphericalScratch = new THREE.Spherical();
    if (!this.#returnOffsetScratch) this.#returnOffsetScratch = new THREE.Vector3();
    const offset = this.#returnOffsetScratch.subVectors(this.#camera.position, this.#controls.target);
    this.#returnSphericalScratch.setFromVector3(offset);
    if (Math.abs(this.#returnSphericalScratch.phi - UPRIGHT_PHI) < 0.005) return;
    this.#returning = true;
    this.#returnStartedAt = performance.now();
    this.#returnFromPhi = this.#returnSphericalScratch.phi;
  }

  #stepReturn(now) {
    const t = Math.min(1, (now - this.#returnStartedAt) / RETURN_DURATION_MS);
    const eased = 1 - Math.pow(1 - t, 3);
    const phi = this.#returnFromPhi + (UPRIGHT_PHI - this.#returnFromPhi) * eased;
    const offset = this.#returnOffsetScratch.subVectors(this.#camera.position, this.#controls.target);
    this.#returnSphericalScratch.setFromVector3(offset);
    this.#returnSphericalScratch.phi = phi;
    offset.setFromSpherical(this.#returnSphericalScratch);
    this.#camera.position.copy(this.#controls.target).add(offset);
    // Explicit lookAt — controls.update() ran this frame against the
    // pre-overlay position; without this, orientation lags a frame.
    this.#camera.lookAt(this.#controls.target);
    if (t >= 1) this.#returning = false;
  }

  // ── Soda Can comparison mode ─────────────────────────────────────────

  // Idempotent — unbind first so remounts don't stack listeners. Hide the
  // button on purely decorative viewers (hero rotator) — Soda Can only
  // makes sense when something is driving variant state.
  #bindSodaCanToggle() {
    this.#unbindSodaCanToggle();
    const btn = this.#getToggleButton();
    if (!btn) return;
    const scope = this.#scopeRoot || document;
    if (!scope.querySelector('swatch-picker, bunny-premade-paint')) {
      btn.hidden = true;
      return;
    }
    btn.hidden = false;
    this.#onSodaCanToggleBound = (event) => {
      event.preventDefault();
      this.#toggleSodaCan();
    };
    btn.addEventListener('click', this.#onSodaCanToggleBound);
    this.#updateToggleButton();
  }

  #unbindSodaCanToggle() {
    if (!this.#onSodaCanToggleBound) return;
    const btn = this.#getToggleButton();
    if (btn) btn.removeEventListener('click', this.#onSodaCanToggleBound);
    this.#onSodaCanToggleBound = null;
  }

  #getToggleButton() {
    return this.querySelector('[data-soda-can-toggle]');
  }

  #updateToggleButton() {
    const btn = this.#getToggleButton();
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(this.#sodaCanMode));
    const label = btn.querySelector('.bunny-3d-viewer__mode-toggle-label');
    if (label) {
      label.textContent = this.#sodaCanMode ? 'Remove the Can' : 'Spawn the Can';
    }
  }

  // Lazy single-fetch; per-viewer copies via .clone(true). Promise is
  // cleared on rejection so a transient network failure doesn't permanently
  // disable Soda Can — next call retries.
  #ensureSodaCanLoaded() {
    if (!Bunny3dViewer.#sodaCanGltfPromise) {
      const loader = new GLTFLoader();
      const draco = new DRACOLoader();
      draco.setDecoderPath(DRACO_DECODER_PATH);
      loader.setDRACOLoader(draco);
      const inflight = loader.loadAsync(SODA_CAN_URL).then((gltf) => {
        // Defensive normals (matches the toy GLB path).
        gltf.scene.traverse((child) => {
          if (child.isMesh && child.geometry && !child.geometry.attributes.normal) {
            child.geometry.computeVertexNormals();
          }
        });
        return gltf.scene;
      });
      inflight.catch(() => {
        if (Bunny3dViewer.#sodaCanGltfPromise === inflight) {
          Bunny3dViewer.#sodaCanGltfPromise = null;
        }
      });
      Bunny3dViewer.#sodaCanGltfPromise = inflight;
    }
    return Bunny3dViewer.#sodaCanGltfPromise;
  }

  #toggleSodaCan() {
    this.#sodaCanMode = !this.#sodaCanMode;
    this.#updateToggleButton();
    this.#applySodaCanLayout(this.#sodaCanMode, true);
  }

  // On PDP the gallery morphs the viewer host (and #adoptCachedState reads
  // the new dataset). Hero + configurator don't morph the host, so read the
  // new toy length from the event's server-rendered HTML directly.
  #onVariantUpdate(event) {
    if (!this.#originMatches(event)) return;
    const html = event.detail?.data?.html;
    if (!html) return;
    const newViewer = html.querySelector('bunny-3d-viewer');
    if (!newViewer) return;
    const newAttr = newViewer.dataset.toyLengthIn;
    if (newAttr) {
      this.dataset.toyLengthIn = newAttr;
    } else {
      delete this.dataset.toyLengthIn;
    }
    this.#toyLengthIn = this.#readToyLengthInches();
    if (this.#sodaCanMode) this.#applySodaCanLayout(true, true);
  }

  // Toggle-on: ensure GLB loaded, rescale, set tween targets.
  // Toggle-off: tween toy + camera back; can stays mid-tween, #stepToggleTween
  // removes it on completion.
  async #applySodaCanLayout(on, animated) {
    if (!this.#toyRoot) return;

    if (on && !this.#sodaCanRoot) {
      let baseScene;
      try {
        baseScene = await this.#ensureSodaCanLoaded();
      } catch (err) {
        console.warn('[bunny-3d-viewer] soda can load failed', err);
        this.#sodaCanMode = false;
        this.#updateToggleButton();
        return;
      }
      // The user may have toggled off while the GLB was downloading.
      if (!this.#sodaCanMode) return;
      const canScene = baseScene.clone(true);
      canScene.scale.setScalar(1);
      canScene.position.set(0, 0, 0);
      canScene.updateMatrixWorld(true);
      const nativeBbox = new THREE.Box3().setFromObject(canScene);
      canScene.userData.nativeBbox = nativeBbox;
      canScene.userData.nativeCenter = nativeBbox.getCenter(new THREE.Vector3());
      this.#sodaCanRoot = canScene;
    }

    if (on && this.#sodaCanRoot && !this.#sodaCanRoot.parent) {
      this.#scene.add(this.#sodaCanRoot);
    }

    const canTargets = on ? this.#computeCanScaleTargets() : null;
    // First-time sizing snaps to sized state so toggle-on doesn't ramp from
    // native scale (tracked via #sodaCanWidthWorld === 0).
    const firstTimeSize = on && canTargets && this.#sodaCanWidthWorld === 0;
    if (firstTimeSize) {
      this.#sodaCanRoot.scale.setScalar(canTargets.scale);
      this.#sodaCanRoot.position.y = canTargets.y;
      this.#sodaCanRoot.position.z = canTargets.z;
      this.#sodaCanWidthWorld = canTargets.widthWorld;
    } else if (canTargets) {
      this.#sodaCanWidthWorld = canTargets.widthWorld;
    }

    // Toy at +A, can at -A → gap_wu = 2A − (toyW + canW)/2.
    // Solve for A given the target gap (in real-world inches × wu/in).
    const wuPerInch =
      Number.isFinite(this.#toyLengthIn) && this.#toyLengthIn > 0 && this.#toyHeightWorld > 0
        ? this.#toyHeightWorld / this.#toyLengthIn
        : 1;
    const gapWorld = SODA_CAN_GAP_IN * wuPerInch;
    const offsetX = gapWorld / 2 + (this.#toyWidthWorld + this.#sodaCanWidthWorld) / 4;
    const targetToyX = on ? this.#toyCenteredX + offsetX : this.#toyCenteredX;
    const targetDistance = on ? CAMERA_DISTANCE_PAIR : CAMERA_DISTANCE_SOLO;
    const targetCanX = -offsetX;

    // Pre-position can.x on first-time so toggle-on doesn't slide it out
    // from under the toy.
    if (firstTimeSize) {
      this.#sodaCanRoot.position.x = targetCanX;
    }

    if (animated) {
      this.#toggleTweenFromDistance = this.#cameraDistance();
      this.#toggleTweenToDistance = targetDistance;
      this.#toggleTweenFromToyX = this.#toyRoot.position.x;
      this.#toggleTweenToToyX = targetToyX;
      if (this.#sodaCanRoot) {
        this.#toggleTweenFromCanX = this.#sodaCanRoot.position.x;
        // Toggle-off: leave can in place during tween; camera pulls in to
        // crop it. #stepToggleTween removes the can on completion.
        this.#toggleTweenToCanX = on ? targetCanX : this.#sodaCanRoot.position.x;
        this.#toggleTweenFromCanY = this.#sodaCanRoot.position.y;
        this.#toggleTweenToCanY = on && canTargets ? canTargets.y : this.#sodaCanRoot.position.y;
        this.#toggleTweenFromCanScale = this.#sodaCanRoot.scale.x;
        this.#toggleTweenToCanScale = on && canTargets ? canTargets.scale : this.#sodaCanRoot.scale.x;
      }
      this.#toggleTweenStart = performance.now();
    } else {
      this.#setCameraDistance(targetDistance);
      this.#toyRoot.position.x = targetToyX;
      if (this.#sodaCanRoot) {
        this.#sodaCanRoot.position.x = targetCanX;
        if (on && canTargets) {
          this.#sodaCanRoot.scale.setScalar(canTargets.scale);
          this.#sodaCanRoot.position.y = canTargets.y;
          this.#sodaCanRoot.position.z = canTargets.z;
        }
      }
      if (!on && this.#sodaCanRoot && this.#sodaCanRoot.parent) {
        this.#scene.remove(this.#sodaCanRoot);
      }
      this.#toggleTweenStart = 0;
    }
  }

  // Pure: returns target scale/Y/Z/width without mutating the scene.
  // canHeight_wu = SODA_CAN_HEIGHT_IN × wu/in. Align can base to toy base
  // (toy centered at origin → toy base at -toyHeightWorld/2).
  #computeCanScaleTargets() {
    if (!this.#sodaCanRoot) return null;
    if (!this.#toyHeightWorld) return null;
    if (!Number.isFinite(this.#toyLengthIn) || this.#toyLengthIn <= 0) return null;
    const native = this.#sodaCanRoot.userData.nativeBbox;
    const nativeCenter = this.#sodaCanRoot.userData.nativeCenter;
    if (!native || !nativeCenter) return null;
    const nativeSize = native.getSize(new THREE.Vector3());
    if (!nativeSize.y) return null;

    const wuPerInch = this.#toyHeightWorld / this.#toyLengthIn;
    const targetCanHeight = SODA_CAN_HEIGHT_IN * wuPerInch;
    const scale = targetCanHeight / nativeSize.y;
    const widthWorld = nativeSize.x * scale;
    const toyBaseY = -this.#toyHeightWorld / 2;
    const canCenterY = toyBaseY + targetCanHeight / 2;
    return {
      scale,
      y: canCenterY - nativeCenter.y * scale,
      z: -nativeCenter.z * scale,
      widthWorld,
    };
  }

  // Cubic ease toggle tween: camera distance + toy-x + can-x/y/scale on
  // a shared clock. Removes can from scene on toggle-off completion.
  #stepToggleTween(now) {
    const t = Math.min(1, (now - this.#toggleTweenStart) / TOGGLE_TWEEN_MS);
    const eased = 1 - Math.pow(1 - t, 3);
    const lerp = (from, to) => from + (to - from) * eased;
    this.#setCameraDistance(lerp(this.#toggleTweenFromDistance, this.#toggleTweenToDistance));
    if (this.#toyRoot) {
      this.#toyRoot.position.x = lerp(this.#toggleTweenFromToyX, this.#toggleTweenToToyX);
    }
    if (this.#sodaCanRoot) {
      this.#sodaCanRoot.position.x = lerp(this.#toggleTweenFromCanX, this.#toggleTweenToCanX);
      this.#sodaCanRoot.position.y = lerp(this.#toggleTweenFromCanY, this.#toggleTweenToCanY);
      const scale = lerp(this.#toggleTweenFromCanScale, this.#toggleTweenToCanScale);
      this.#sodaCanRoot.scale.setScalar(scale);
    }
    if (t >= 1) {
      this.#toggleTweenStart = 0;
      if (!this.#sodaCanMode && this.#sodaCanRoot && this.#sodaCanRoot.parent) {
        this.#scene.remove(this.#sodaCanRoot);
      }
    }
  }

  // Radius from orbit target — the meaningful zoom level. position.z only
  // equals the radius when the camera happens to be on +Z.
  #cameraDistance() {
    if (!this.#camera || !this.#controls) return 0;
    return this.#camera.position.distanceTo(this.#controls.target);
  }

  // Scale the offset vector to preserve azimuth/polar — the safe way to
  // "zoom" while OrbitControls is rotating. Uses a per-instance scratch
  // (separate from #returnOffsetScratch — both tweens can run in the same
  // #tick) to avoid allocating a fresh Vector3 each call.
  #setCameraDistance(distance) {
    if (!this.#camera || !this.#controls) return;
    if (!this.#cameraOffsetScratch) this.#cameraOffsetScratch = new THREE.Vector3();
    const offset = this.#cameraOffsetScratch.subVectors(this.#camera.position, this.#controls.target);
    const len = offset.length();
    if (len === 0) {
      // Degenerate: camera at target. Push out along +Z for a defined direction.
      this.#camera.position.copy(this.#controls.target);
      this.#camera.position.z += distance;
      return;
    }
    offset.multiplyScalar(distance / len);
    this.#camera.position.copy(this.#controls.target).add(offset);
  }

  // Read variant's `total_length_in` with a fallback so toggle stays
  // functional on un-authored variants (rough-but-real comparison vs. disabled UX).
  #readToyLengthInches() {
    const attr = this.dataset.toyLengthIn;
    if (!attr) return FALLBACK_TOY_LENGTH_IN;
    const parsed = parseFloat(attr);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : FALLBACK_TOY_LENGTH_IN;
  }
}

if (!customElements.get('bunny-3d-viewer')) {
  customElements.define('bunny-3d-viewer', Bunny3dViewer);
}
